import React, { useEffect, useState } from "react";
import clsx from "clsx";
import {
  TextField,
  Typography,
  Grid,
  InputLabel,
  TextareaAutosize,
  FormControlLabel,
  Checkbox,
  Button,
  LinearProgress,
  useMediaQuery,
  Theme,
  Snackbar,
} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { LinkKeepUTM } from "../../../../../shared/components/LinkKeepUTM";
import { useHistory } from "../../../../../shared/hooks/useHistory";
import DateFnsUtils from "@date-io/date-fns";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import { Alert } from "@material-ui/lab";
import { Header } from "../../../../../shared/components/header";
import { buildBreadcrumbNavigationConfig } from "../../../../../shared/helpers/buildBreadcrumbNavigationConfig";
import {
  asyncDeleteParticipantTodoById,
  asyncGetParticipantTodo,
  asyncUpdateParticipantTodo,
} from "../../../../../todos/actions/todoActions";
import useStyle from "./edit-task.styles";
import { RootState } from "../../../../../store";
import { Task } from "../../../../../models/redux/task.model";
import { CategoriesDropdown } from "../../../../../shared/components/categoriesDropdown";
import { TrashIcon } from "../../../../../shared/icons";
import { CustomModal } from "../../../../../common/modal";
import useModal from "../../../../../shared/hooks/useModal";
import { useStyles as useModalStyles } from "./edit-task-modal.style";
import { useAlertMessage } from "../../../../../shared/hooks/useAlertMessage";

interface Request {
  isFetching: boolean;
  type: string;
  statusCode: number;
  message: string;
  isOpen: boolean;
}

interface Selector {
  request: Request;
  task: Task;
  saveMsg: {
    isOpen: boolean;
    message: string;
    type: string;
  };
}

interface Params {
  taskId: string;
}

function EditTask() {
  const dispatch = useDispatch();
  const { taskId } = useParams<Params>();
  const history = useHistory();
  const sm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const classes = useStyle();
  const modalClasses = useModalStyles();
  const [taskState, setTaskState] = useState<Task>();
  const { isShowing, toggle } = useModal();

  const { isOpen, openAlertMessage, handleClose } = useAlertMessage();

  const { request, task, saveMsg } = useSelector<RootState, Selector>(
    (state) => {
      return {
        request: state.request.request,
        task: state.todosReducer.currentTodo,
        saveMsg: state.todosReducer.successSaveTodoParticpant,
      };
    }
  );

  useEffect(() => {
    dispatch(asyncGetParticipantTodo(taskId));
  }, [taskId, dispatch]);

  useEffect(() => {
    if (task) setTaskState({ ...task });
  }, [task]);

  const navigation = [
    buildBreadcrumbNavigationConfig("Tasks", "/tasks"),
    buildBreadcrumbNavigationConfig(
      `task: ${task.todoId}`,
      `/tasks/${task.todoId}`
    ),
    buildBreadcrumbNavigationConfig("Edit Task"),
  ];

  const handleChange = ({ target }) =>
    setTaskState((state) => ({ ...state, ...{ [target.name]: target.value } }));

  const handleCheckChange = (e) =>
    setTaskState({ ...taskState, ...{ completed: e.target.checked } });

  const handleDueDateChange = (dueDate) =>
    setTaskState({ ...taskState, dueDate });

  const handleCategoryChange = (categoryId) =>
    setTaskState({ ...taskState, categoryId });

  const handleSubmit = async () => {
    await dispatch(asyncUpdateParticipantTodo(taskState));
    openAlertMessage();
    history.push("/participant/tasks");
  };

  const handleDelete = (): void => {
    dispatch(asyncDeleteParticipantTodoById(task.todoId));
    history.push("/participant/tasks");
  };

  const modalActions = [
    {
      text: "Cancel",
      onClick: toggle,
      className: modalClasses.btnCancel,
    },
    {
      text: "Yes, delete task",
      onClick: handleDelete,
      className: modalClasses.btnConfirm,
    },
  ];

  const participantTaskOrigin = 1;
  const isDisabled = task.originId !== participantTaskOrigin;

  return (
    <Header navigation={navigation}>
      <CustomModal
        title="Delete Task"
        classes={modalClasses}
        open={isShowing}
        onClose={toggle}
        actions={modalActions}
      >
        <Typography className={modalClasses.modalText}>
          You are about to delete {`'${taskState?.title}'`}. Are you sure you
          want to continue?
        </Typography>
      </CustomModal>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={isOpen}
        autoHideDuration={3000}
        onClose={handleClose}
        className={
          saveMsg.type !== "success"
            ? classes.snackbarError
            : classes.snackbarSuccess
        }
      >
        <Alert
          onClose={handleClose}
          severity={saveMsg.type !== "success" ? "error" : "success"}
          className={classes.alert}
        >
          {saveMsg.message}
        </Alert>
      </Snackbar>
      <Grid container className={classes.titleContainer}>
        <Grid item xs={10}>
          <Typography variant="h1" className={classes.title}>
            Edit Task
          </Typography>
        </Grid>
        <Grid item xs={2} className={classes.actions}>
          <Button
            className={clsx(classes.button, classes.btnDelete)}
            startIcon={<TrashIcon />}
            disabled={isDisabled}
            onClick={toggle}
          >
            {!sm && "Delete Task"}
          </Button>
        </Grid>
      </Grid>
      {request.isFetching && (
        <div className={classes.root}>
          <LinearProgress color="primary" />
        </div>
      )}
      <Grid container spacing={4} direction="column" className={classes.form}>
        <Grid item xs={12} md={3}>
          <TextField
            variant="outlined"
            value={taskState?.title}
            label="Title"
            name="title"
            id="title"
            onChange={handleChange}
            className={classes.field}
            inputProps={{ "data-testId": "title" }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <CategoriesDropdown
            label="Category"
            name="categoryId"
            value={taskState?.categoryId || -1}
            placeholder="Choose a category"
            onChange={handleCategoryChange}
            className={classes.select}
            inputProps={{ "data-testId": "category" }}
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <InputLabel className={classes.label}>Description</InputLabel>
          <TextareaAutosize
            className={classes.textArea}
            minRows={7}
            defaultValue={taskState?.description}
            name="description"
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <InputLabel className={classes.label}>Due Date</InputLabel>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
              autoOk
              name="dueDate"
              onChange={handleDueDateChange}
              inputVariant="outlined"
              value={taskState?.dueDate}
              className={classes.datePicker}
              views={["year", "month", "date"]}
              variant="inline"
              inputProps={{ "data-testId": "dueDate" }}
              format="MM-dd-yy"
              id="dueDate"
            />
          </MuiPickersUtilsProvider>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            label="Mark as done"
            className={classes.checkbox}
            control={
              <Checkbox
                onChange={handleCheckChange}
                checked={taskState?.completed || false}
              />
            }
          />
        </Grid>
        <Grid item className={classes.footerButtons}>
          <LinkKeepUTM
            to={`/participant/tasks/${task.todoId}`}
            className={clsx(classes.button, classes.btnCancel)}
          >
            Cancel
          </LinkKeepUTM>
          <Button
            onClick={handleSubmit}
            disabled={isDisabled || task?.completed}
            className={clsx(classes.button, classes.btnSave)}
          >
            Save Changes
          </Button>
        </Grid>
      </Grid>
    </Header>
  );
}

export default EditTask;
